import * as Sentry from "@sentry/browser";
import { get_cart_token } from "@/services/woocommerce/cart-service";

export const SENTRY_ERROR_CATEGORIES = {
  API_COMMUNICATION: "API_COMMUNICATION",
  VALIDATION: "VALIDATION",
  STATE_MANAGEMENT: "STATE_MANAGEMENT",
  UI_RENDERING: "UI_RENDERING",
  THIRD_PARTY_INTEGRATION: "THIRD_PARTY_INTEGRATION",
} as const;
export type SentryErrorCategory =
  (typeof SENTRY_ERROR_CATEGORIES)[keyof typeof SENTRY_ERROR_CATEGORIES];

const get_cart_total_from_display = (): number => {
  try {
    const el = document.getElementById("total-display");
    const text = el?.textContent?.trim() || "";
    const numeric = text.replace(/[^0-9.-]/g, "");
    const parsed = parseFloat(numeric);
    return isNaN(parsed) ? 0 : parsed;
  } catch {
    return 0;
  }
};

const get_browser_info = () => {
  try {
    return {
      user_agent: navigator.userAgent,
      language: navigator.language,
      platform: navigator.platform,
      vendor: navigator.vendor,
      online: navigator.onLine,
      viewport: { width: window.innerWidth, height: window.innerHeight },
    };
  } catch {
    return {};
  }
};

const get_user_session_data = () => {
  try {
    const email_input = document.getElementById(
      "billing_email",
    ) as HTMLInputElement | null;
    const email = email_input?.value?.trim();
    return {
      cart_token: get_cart_token(),
      email,
      locale: document.documentElement.lang,
      timestamp: new Date().toISOString(),
    };
  } catch {
    return {};
  }
};

const get_application_state = () => {
  try {
    const params = new URLSearchParams(window.location.search);
    const variation_id = params.get("variation_id");
    const selected = document.querySelector(
      'input[name="payment_method"]:checked',
    ) as HTMLInputElement | null;
    return {
      cart_total: get_cart_total_from_display(),
      selected_payment_method: selected?.value,
      url_params: Object.fromEntries(params.entries()),
      variation_id,
    };
  } catch {
    return {};
  }
};

export const log_checkout_event = (
  log_category: string,
  message: string,
  data?: unknown,
) => {
  const log_attributes: Record<string, unknown> = {
    category: log_category,
    flow: "checkout",
    ...(data as Record<string, unknown>),
  };
  Sentry.logger.info(message, log_attributes);
};

export const log_gateway_event = (
  level: "trace" | "debug" | "info" | "warn" | "error" | "fatal",
  gateway: string,
  step: string,
  message: string,
  data?: Record<string, unknown>,
) => {
  const attributes = {
    flow: "gateway",
    gateway,
    step,
    timestamp: new Date().toISOString(),
    ...(data || {}),
  } as Record<string, unknown>;
  switch (level) {
    case "trace":
      Sentry.logger.trace(message, attributes);
      break;
    case "debug":
      Sentry.logger.debug(message, attributes);
      break;
    case "info":
      Sentry.logger.info(message, attributes);
      break;
    case "warn":
      Sentry.logger.warn(message, attributes);
      break;
    case "error":
      Sentry.logger.error(message, attributes);
      break;
    case "fatal":
      Sentry.logger.fatal(message, attributes);
      break;
  }
};

export const report_gateway_error = ({
  error,
  gateway,
  step,
  message,
  severity = "error",
  extras,
}: {
  error?: unknown;
  gateway: string;
  step: string;
  message?: string;
  severity?: "error" | "warning" | "info";
  extras?: Record<string, unknown>;
}) => {
  Sentry.withScope((scope) => {
    scope.setLevel(severity);
    scope.setExtras({
      flow: "gateway",
      gateway,
      step,
      timestamp: new Date().toISOString(),
      ...(extras || {}),
    });
    if (error instanceof Error) {
      Sentry.captureException(error);
    } else if (message) {
      Sentry.captureMessage(message, severity);
    } else {
      Sentry.captureMessage("Gateway error", severity);
    }
  });
};

export const report_error = ({
  error,
  message,
  category,
  functionName,
  network,
  businessContext,
  severity = "error",
}: {
  error?: unknown;
  message?: string;
  category: SentryErrorCategory;
  functionName: string;
  network?: Record<string, unknown> | undefined;
  businessContext?: Record<string, unknown> | undefined;
  severity?: "error" | "warning" | "info";
}) => {
  try {
    const user_session = get_user_session_data();
    const app_state = get_application_state();
    const browser = get_browser_info();

    Sentry.withScope((scope) => {
      scope.setLevel(severity);
      scope.setTag("flow", "checkout");
      scope.setTag("error_category", category);
      scope.setTag("file", "checkout-page.client.ts");

      if ((user_session as any)?.email) {
        scope.setUser({ email: String((user_session as any).email) });
      }

      scope.setExtras({
        user_session,
        app_state,
        browser,
        network,
        business_context: businessContext,
        severity,
        function_name: functionName,
      });

      if (error instanceof Error) {
        Sentry.captureException(error);
      } else if (message) {
        Sentry.captureMessage(message, severity);
      } else {
        Sentry.captureMessage("Unknown error", severity);
      }
    });
  } catch (report_err) {
    console.error("Failed to report error to Sentry", report_err);
  }
};
